"use client";

import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { ChevronLeft, Menu as MenuIcon } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetPath } from "@/hooks/useGetPath";
import { useStartPageKeyboardViewport } from "@/hooks/useStartPageKeyboardViewport";
import BottomNavigation from "@/components/BottomNavigation"
import { MobileBottomNavSlotContext, MobileBottomNavSlotSetterContext, MobileBottomNavVisibilityContext, MobileSidebarContext, MobileTopBarSlotContext, MobileTopBarSlotSetterContext } from "@/contexts/MobileSidebarContext";
import { MobileBatchChromeContext } from "@/contexts/MobileBatchChromeContext";
import { useWhiteLabel } from "@/contexts/WhiteLabelContext"
import { APP_FONT_STACK } from "@/lib/appFont";
import { useCredits } from "@/contexts/CreditContext"
import NewSideBar from "./NewSideBar";

interface TopBarProps {
  onToggleSidebar: () => void;
  compactDatasetHeader?: boolean;
  /** Workbench phone chrome: the wordmark leaves the run for Examples. */
  onLogo?: () => void;
  /** Workbench phone chrome: explicit back control, same destination as the system back gesture. */
  onBack?: () => void;
}

type SideBarProps = {
  content: React.ReactNode;
  defaultCollapsed?: boolean;
};

const TopBar: React.FC<TopBarProps> = ({ onToggleSidebar, onLogo, onBack, compactDatasetHeader = false }) => {
  const { config } = useWhiteLabel();
  const { profile } = useCredits();
  const setTopBarSlot = React.useContext(MobileTopBarSlotSetterContext);
  const isWhiteLabel = Boolean(config?.isEnabled || /* profile?.org_id === 'org_86fc9717efd9' || */ profile?.org_id === 'org_atlas_pro');
  const isDarkTheme = config?.isDarkTheme;
  const backgroundColor = config?.backgroundColor;

  // Mobile chrome: the logo opens the nav drawer. The slot is the top-right
  // corner so page actions (the dataset agent) dock opposite the wordmark.
  return (
    <div
      className={`sticky top-0 z-40 flex h-12 shrink-0 items-center bg-[#FCFAF4] px-3 lg:hidden ${compactDatasetHeader ? "overflow-hidden" : ""}`}
      style={isDarkTheme && backgroundColor ? { backgroundColor } : undefined}
    >
      {isWhiteLabel ? (
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${isDarkTheme ? 'text-gray-200 hover:bg-white/10' : 'text-[#1F2753] hover:bg-black/5'}`}
            onClick={onLogo ?? onToggleSidebar}
            title={onLogo ? "Examples" : "Open navigation menu"}
          >
            <MenuIcon className="h-5 w-5" aria-hidden="true" />
            <span className="sr-only">Open navigation menu</span>
          </button>
          <a href="/" className="flex min-w-0 items-center">
            <img src={config.logoUrl} alt={config.companyName} className="h-5 w-auto" />
            <span className={`ml-1.5 truncate text-xs font-semibold ${isDarkTheme ? 'text-white' : 'text-gray-900'}`}>
              {config.companyName}
            </span>
          </a>
        </div>
      ) : (
        <button
          type="button"
          className={`flex min-w-0 items-center transition-opacity hover:opacity-90 ${compactDatasetHeader ? "shrink-0 gap-1.5" : "gap-2"}`}
          onClick={onLogo ?? onToggleSidebar}
          title={onLogo ? "Examples" : "Open navigation menu"}
        >
          <img src="/nomadic-app-icon.svg" alt="" aria-hidden className={compactDatasetHeader ? "h-7 w-7 shrink-0" : "h-8 w-8 shrink-0"} />
          <span
            className={`truncate font-semibold leading-none text-[#1F2753] ${compactDatasetHeader ? "text-xs" : "text-sm"}`}
            style={{ fontFamily: APP_FONT_STACK, letterSpacing: "0.02em" }}
          >
            Nomadic AI
          </span>
        </button>
      )}
      <div className={`ml-auto flex items-center gap-1 ${compactDatasetHeader ? "min-w-0 flex-1 justify-end" : "shrink-0"}`}>
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            title="Examples"
            aria-label="Back"
            className="grid h-7 w-7 place-items-center rounded-md transition-colors"
            style={{
              background: "#FFFFFF",
              border: "1px solid rgba(31,39,83,0.08)",
              boxShadow: "0 1px 2px rgba(31,39,83,0.04), 0 1px 1px rgba(31,39,83,0.03)",
              color: "#1F2753",
            }}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
        ) : null}
        <div ref={setTopBarSlot} className={compactDatasetHeader ? "flex min-w-0 flex-1 items-center justify-end" : "flex shrink-0 items-center"} />
      </div>
    </div>
  );
};


// Remembers the user's collapse choice across per-route SideBar remounts within
// a session (null = untouched → fall back to defaultCollapsed). Resets on reload.
let sessionCollapsed: boolean | null = null;

/**
 * Phone back (the control and the system gesture) leaves a workbench for the
 * page that opened it. A run opened on its own has no previous entry, so
 * Examples is placed behind it and the gesture returns there.
 */
function useWorkbenchBack(enabled: boolean, getPath: (path: string) => string) {
  const navigate = useNavigate();
  const seeded = useRef(false);

  useEffect(() => {
    if (!enabled || seeded.current) return;
    seeded.current = true;
    const idx = window.history.state?.idx;
    if (typeof idx === "number" && idx > 0) return;
    const examplesPath = getPath("/examples");
    const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const current = window.history.state;
    const priorUserState =
      current?.usr && typeof current.usr === "object" ? current.usr : {};
    window.history.replaceState({ idx: 0, key: "examples", usr: null }, "", examplesPath);
    window.history.pushState(
      {
        ...(current && typeof current === "object" ? current : {}),
        idx: 1,
        usr: { ...priorUserState, workbenchExamplesBehind: true },
      },
      "",
      here,
    );
  }, [enabled, getPath]);

  return () => {
    const idx = window.history.state?.idx;
    if (typeof idx === "number" && idx > 0) {
      navigate(-1);
      return;
    }
    navigate(getPath("/examples"));
  };
}

export default function SideBar({ content, defaultCollapsed = false }: SideBarProps) {
  const getPath = useGetPath();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [topBarSlot, setTopBarSlot] = useState<HTMLDivElement | null>(null);
  const [bottomNavSlot, setBottomNavSlot] = useState<HTMLDivElement | null>(null);
  const [mobileBottomNavHidden, setMobileBottomNavHidden] = useState(false);
  const location = useLocation();
  const normalizedPath = location.pathname.startsWith("/live-demo")
    ? location.pathname.slice("/live-demo".length) || "/"
    : location.pathname;
  const [batchUnifiedHeaderActive, setBatchUnifiedHeaderActive] = useState(false);
  const hideMobileTopBar =
    /^\/use-cases\/rapid-review\/batch-view\/[^/]+$/.test(normalizedPath) &&
    batchUnifiedHeaderActive;
  // The workbench fills the space under the Nomadic bar. svh stays the same
  // size when Android shows or hides the URL bar; dvh resizes that shell while
  // an inner list scrolls and the sticky header and fixed nav shake.
  const workbenchRoute = /^\/workbench\//.test(normalizedPath);
  const fixedStartPage = normalizedPath === "/" || normalizedPath === "/start-page";
  const datasetDetailRoute = /^\/dataset-studio\/[^/]+(?:\/distribution-new)?\/?$/.test(normalizedPath);
  const keyboardViewport = useStartPageKeyboardViewport(fixedStartPage || datasetDetailRoute || workbenchRoute);
  const leaveWorkbench = useWorkbenchBack(workbenchRoute, getPath);
  // Each route wraps its own <Layout>, so this SideBar remounts on every
  // navigation. To keep the collapsed/expanded choice across tab changes we read
  // it from a module-scoped variable that survives those remounts. The nav is
  // collapsed by default and, once the user expands it, stays open while they
  // navigate; a full page reload resets it to the default (closed).
  const [isCollapsed, setIsCollapsed] = useState(() => sessionCollapsed ?? defaultCollapsed);

  const toogleSidebar = () => setSidebarOpen(prev => !prev);
  const toggleCollapse = () =>
    setIsCollapsed(prev => {
      const next = !prev;
      sessionCollapsed = next;
      return next;
    });

  const { config } = useWhiteLabel();
  const { profile } = useCredits();
  const isWhiteLabel = Boolean(config?.isEnabled || /* profile?.org_id === 'org_86fc9717efd9' || */ profile?.org_id === 'org_atlas_pro');
  return (
    <MobileSidebarContext.Provider value={{ open: () => setSidebarOpen(true) }}>
    <MobileTopBarSlotContext.Provider value={topBarSlot}>
    <MobileTopBarSlotSetterContext.Provider value={setTopBarSlot}>
    <MobileBottomNavSlotContext.Provider value={bottomNavSlot}>
    <MobileBottomNavSlotSetterContext.Provider value={setBottomNavSlot}>
    <MobileBottomNavVisibilityContext.Provider value={{ hidden: mobileBottomNavHidden || Boolean(keyboardViewport), setHidden: setMobileBottomNavHidden }}>
    <MobileBatchChromeContext.Provider
      value={{
        unifiedHeaderActive: batchUnifiedHeaderActive,
        setUnifiedHeaderActive: setBatchUnifiedHeaderActive,
      }}
    >
    <div data-start-keyboard={keyboardViewport ? "true" : undefined} style={keyboardViewport ? { position: "fixed", top: keyboardViewport.top, left: 0, right: 0, height: keyboardViewport.height, background: "#FCFAF4", overflow: "hidden" } : undefined}>
      <Transition show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          // Above page chrome (TopBar z-40), walkthrough pills (z-70), and
          // auth modal (z-200) so the mobile drawer truly covers the screen.
          className="relative z-[300] lg:hidden"
          onClose={setSidebarOpen}
        >
          <TransitionChild
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </TransitionChild>

          <div className="fixed inset-0 flex z-10">
            <TransitionChild
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              {/* Nearly full-bleed drawer: everything but a 32px strip, which
                  doubles as the tap-outside-to-close target. */}
              <DialogPanel className="relative flex w-[calc(100vw-32px)] max-w-none">
                <NewSideBar
                  onNavigate={() => setSidebarOpen(false)}
                  onClose={() => setSidebarOpen(false)}
                />
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>

      {/* Floating menu button for mobile — batch viewer renders its own unified header */}
      {!hideMobileTopBar && (
        <TopBar
          onToggleSidebar={toogleSidebar}
          onBack={workbenchRoute ? leaveWorkbench : undefined}
          onLogo={workbenchRoute && !isWhiteLabel ? leaveWorkbench : undefined}
          compactDatasetHeader={datasetDetailRoute}
        />
      )}

      {/* Static sidebar for desktop */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex ${isCollapsed ? 'lg:w-16' : (isWhiteLabel ? 'lg:w-80' : 'lg:w-48')} lg:flex-col transition-all duration-300`}>
        <div className="flex grow flex-col gap-y-5 overflow-y-auto pb-0">
          <NewSideBar onCollapse={toggleCollapse} isCollapsed={isCollapsed} />
        </div>
      </div>

      <div className={`${isCollapsed ? 'lg:pl-16' : (isWhiteLabel ? "lg:pl-80" : "lg:pl-48")} transition-all duration-300`}>
        {/* Extra bottom room on mobile: the nav floats, so it needs clearance
            for its own inset and shadow on top of its height. The surface color
            has to live here too — otherwise that padding paints as a white band
            below the page once you scroll to the end. */}
        <main
          style={keyboardViewport ? { height: `calc(${keyboardViewport.height}px - 3rem)`, paddingBottom: 0 } : undefined}
          className={
            fixedStartPage
              ? "h-[calc(100dvh-3rem)] overflow-clip bg-[#FCFAF4] pb-20 sm:pb-24 lg:h-dvh lg:pb-0"
              : datasetDetailRoute
              ? "h-[calc(100dvh-3rem)] overflow-hidden bg-[#FCFAF4] lg:h-auto lg:overflow-visible"
              : workbenchRoute
              ? "h-[calc(100svh-3rem)] overflow-hidden overscroll-none bg-[#FCFAF4] lg:h-auto lg:overflow-visible lg:bg-transparent"
              : "overflow-y-auto bg-[#FCFAF4] pb-24 lg:bg-transparent lg:pb-0"
          }
        >
          <div className={(fixedStartPage || datasetDetailRoute) ? "h-full min-h-0" : workbenchRoute ? "h-full min-h-0 lg:h-auto" : undefined}>{content}</div>
        </main>
      </div>

      {/* Bottom Navigation for Mobile and Medium Screens */}
      <BottomNavigation forceHidden={Boolean(keyboardViewport)} />
    </div>
    </MobileBatchChromeContext.Provider>
    </MobileBottomNavVisibilityContext.Provider>
    </MobileBottomNavSlotSetterContext.Provider>
    </MobileBottomNavSlotContext.Provider>
    </MobileTopBarSlotSetterContext.Provider>
    </MobileTopBarSlotContext.Provider>
    </MobileSidebarContext.Provider>
  );
}
